import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { mapApiErrorToUiModel } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { groupTripNoticeApi } from '../../api/groupTripNoticeApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { Button, EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  markAllGroupTripNoticesRead,
  mergeGroupTripNotice,
  normalizeGroupTripNoticePage,
  normalizeGroupTripNoticeUnreadCount,
} from './groupTripNoticeModel';
import { notifyGroupTripNoticeUnreadChanged } from './groupTripNoticeUnreadBus';
import NotificationCenterTabs from '../notification-center/NotificationCenterTabs';
import './group-trip-notice.css';

const PAGE_LIMIT = 20;

function NoticeItem({ notice, markingRead, onMarkRead, onOpen, t }) {
  const busy = markingRead;
  const badgeLabel = notice.unread
    ? t('travel.groupTripNotice.unread')
    : t('travel.common.read');

  return (
    <li>
      <article
        className={`group-trip-notice__item ${notice.unread ? 'is-unread' : 'is-read'}`}
        aria-labelledby={`gtn-title-${notice.noticeId}`}
      >
        <p className="group-trip-notice__meta">
          <span className={`group-trip-notice__badge${notice.unread ? ' is-unread' : ''}`}>
            {badgeLabel}
          </span>
          {notice.typeLabel ? <span>{notice.typeLabel}</span> : null}
        </p>
        {notice.titleSnapshot ? (
          <h3
            className="group-trip-notice__trip-title"
            id={`gtn-title-${notice.noticeId}`}
          >
            {notice.titleSnapshot}
          </h3>
        ) : null}
        {notice.message ? (
          <p className="group-trip-notice__message">{notice.message}</p>
        ) : null}
        {notice.createTime ? (
          <p className="group-trip-notice__timestamp">
            <time dateTime={notice.createTime}>{formatDateTime(notice.createTime)}</time>
          </p>
        ) : null}
        <div className="group-trip-notice__actions">
          {notice.unread ? (
            <Button
              type="button"
              variant="primary"
              loading={busy}
              onClick={() => onMarkRead(notice)}
            >
              {t('travel.groupTripNotice.markRead')}
            </Button>
          ) : null}
          {notice.deepLink ? (
            <Link
              className="group-trip-notice__open"
              to={notice.deepLink}
              onClick={() => { if (notice.unread) onOpen(notice); }}
            >
              {t('travel.groupTripNotice.openTrip')}
            </Link>
          ) : null}
        </div>
      </article>
    </li>
  );
}

export default function GroupTripNoticesPage() {
  const { t } = useTravelTranslation();
  const generation = useRef(0);
  const mutatingRef = useRef('');
  const loadingMoreRef = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingReadId, setMarkingReadId] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState(null);

  const refreshUnread = useCallback(async () => {
    try {
      const countPayload = await groupTripNoticeApi.getUnreadCount();
      setUnreadCount(normalizeGroupTripNoticeUnreadCount(countPayload).unreadCount);
      notifyGroupTripNoticeUnreadChanged();
    } catch {
      setUnreadCount(null);
      notifyGroupTripNoticeUnreadChanged();
    }
  }, []);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    setActionError('');
    try {
      const [listPayload, countPayload] = await Promise.all([
        groupTripNoticeApi.list({ limit: PAGE_LIMIT }),
        groupTripNoticeApi.getUnreadCount(),
      ]);
      if (requestId !== generation.current) return;
      const page = normalizeGroupTripNoticePage(listPayload, t);
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setUnreadCount(normalizeGroupTripNoticeUnreadCount(countPayload).unreadCount);
      notifyGroupTripNoticeUnreadChanged();
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setErrorModel(mapApiErrorToUiModel(cause, { t }));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  async function loadMore() {
    if (loadingMoreRef.current || loadingMore || !hasMore || !nextCursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setActionError('');
    try {
      const payload = await groupTripNoticeApi.list({
        limit: PAGE_LIMIT,
        cursorCreateTime: nextCursor.cursorCreateTime,
        cursorNoticeId: nextCursor.cursorNoticeId,
      });
      const page = normalizeGroupTripNoticePage(payload, t);
      setItems((current) => {
        const seen = new Set(current.map((item) => item.noticeId));
        return [...current, ...page.items.filter((item) => !seen.has(item.noticeId))];
      });
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch {
      setActionError(t('travel.groupTripNotice.error'));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  async function handleMarkRead(notice) {
    if (mutatingRef.current) return;
    if (!notice.unread) return;
    mutatingRef.current = notice.noticeId;
    setMarkingReadId(notice.noticeId);
    setActionError('');
    setActionStatus('');
    try {
      await groupTripNoticeApi.markRead(notice.noticeId);
      setItems((current) => mergeGroupTripNotice(current, notice.noticeId, {
        noticeStatus: 'READ',
        unread: false,
      }));
      setUnreadCount((prev) => (prev != null && prev > 0 ? prev - 1 : 0));
      setActionStatus(t('travel.groupTripNotice.markRead'));
      notifyGroupTripNoticeUnreadChanged();
    } catch {
      setActionError(t('travel.groupTripNotice.error'));
    } finally {
      mutatingRef.current = '';
      setMarkingReadId('');
    }
  }

  async function handleMarkAll() {
    if (markingAll || mutatingRef.current) return;
    const hasUnread = items.some((item) => item.unread) || (unreadCount != null && unreadCount > 0);
    if (!hasUnread) return;
    mutatingRef.current = 'all';
    setMarkingAll(true);
    setActionError('');
    setActionStatus('');
    try {
      await groupTripNoticeApi.markAllRead();
      setItems((current) => markAllGroupTripNoticesRead(current));
      setUnreadCount(0);
      setActionStatus(t('travel.groupTripNotice.markAllDone'));
      await refreshUnread();
    } catch {
      // 失敗時不假裝全部已讀；保留原 list state
      setActionError(t('travel.groupTripNotice.actionFailed'));
      await refreshUnread();
    } finally {
      mutatingRef.current = '';
      setMarkingAll(false);
    }
  }

  function handleOpen(notice) {
    handleMarkRead(notice);
  }

  if (phase === 'loading') {
    return (
      <main className="group-trip-notice__state" data-testid="group-trip-notices-loading">
        <NotificationCenterTabs active="groupTrip" />
        <LoadingState label={t('travel.common.loading')} />
      </main>
    );
  }

  if (phase === 'error') {
    return (
      <main className="group-trip-notice__state group-trip-notice__state--error" data-testid="group-trip-notices-error">
        <NotificationCenterTabs active="groupTrip" />
        <ErrorState
          title={t('travel.groupTripNotice.title')}
          description={t('travel.groupTripNotice.error')}
          retryable={errorModel ? errorModel.retryable : true}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const markAllEnabled = items.some((item) => item.unread)
    || (unreadCount != null && unreadCount > 0);
  const unreadLabel = unreadCount != null && unreadCount > 0
    ? `${unreadCount} ${t('travel.groupTripNotice.unread')}`
    : '';

  return (
    <main className="group-trip-notice">
      <NotificationCenterTabs active="groupTrip" />
      <header className="group-trip-notice__header">
        <div className="group-trip-notice__heading">
          <h1 className="group-trip-notice__title">{t('travel.groupTripNotice.title')}</h1>
          <p className="group-trip-notice__lead">{t('travel.groupTripNotice.lead')}</p>
          {unreadLabel ? (
            <p className="group-trip-notice__unread-chip" aria-live="polite">{unreadLabel}</p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          className="group-trip-notice__mark-all"
          disabled={!markAllEnabled}
          loading={markingAll}
          onClick={handleMarkAll}
        >
          {t('travel.groupTripNotice.markAll')}
        </Button>
      </header>

      <div className="group-trip-notice__live" aria-live="polite">
        {actionStatus ? <p role="status">{actionStatus}</p> : null}
        {actionError ? <p role="alert">{actionError}</p> : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          className="group-trip-notice__empty"
          title={t('travel.groupTripNotice.empty')}
          description={t('travel.groupTripNotice.emptyDescription')}
        />
      ) : (
        <ul className="group-trip-notice__list">
          {items.map((notice) => (
            <NoticeItem
              key={notice.noticeId}
              notice={notice}
              markingRead={markingReadId === notice.noticeId}
              onMarkRead={handleMarkRead}
              onOpen={handleOpen}
              t={t}
            />
          ))}
        </ul>
      )}

      {hasMore ? (
        <div className="group-trip-notice__load-more-wrap">
          <Button
            type="button"
            variant="secondary"
            className="group-trip-notice__load-more"
            loading={loadingMore}
            onClick={loadMore}
          >
            {t('travel.groupTripNotice.loadMore')}
          </Button>
        </div>
      ) : null}
    </main>
  );
}
